import { FILE_LIST_TYPE } from '@/modules/file/constants'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@mui/styles'

import ThumbnailList from '@/modules/file/components/thumbnail-list/thumbnail-list'
import FileFlatList from './file-flat-list'
import AppStoreBadges from './app-store-badges'
import FolderNav from '@/modules/file/components/folder-nav/folder-nav'

import ThemeInterface from '@/theme-interface'
import { Document } from '@/modules/document/types'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import Panel from '@/components/layouts/panel'
import PanelHeader from '@/components/layouts/panel/panel-header'
import PanelContent from '@/components/layouts/panel/panel-content'
import {
  setPagination,
  clearList,
  setSortOrder,
  setListDisplayType,
  setSearchTerm,
  setActiveFilter,
  setFolder,
} from '@/redux/ducks/document/list'
import FileToolbar from './file-toolbar'
import lang from '@/lang'
import { orderBy as sort } from 'lodash'
import clsx from 'clsx'
import ToolbarButton from '@/components/buttons/toolbar-button'
import ListIcon from '@assets/images/list.svg'
import TilesIcon from '@assets/images/tiles.svg'
import ButtonGroup from '@mui/material/ButtonGroup'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import isMSOfficeDocument from '@/modules/document/_helpers/isOfficeDocument'
import {
  fetchListAction,
  fetchListSetting,
  fetchFolders,
} from '@/redux/ducks/lists/requests/documentList'
import { QUERY_TYPES } from '@/redux/ducks/lists'
import { cancelUploads } from '@/redux/ducks/document/upload'
import { terminateWorker } from '@/redux/ducks/worker'
import { TabInput } from '@/modules/base/components/inputs'
import { useDmsCollaborationToken } from '@/modules/document/_helpers/useDmsCollaborationToken'

const INITIAL_PREVIEW_STATE: { file: Document | null } = { file: null }
const SIZES = Object.freeze({ ICON: '13px' })

export interface PreviewControlHandlers {
  handleClosePreview: () => void,
  handleRemove: () => void,
  handleAbort: () => void,
  handleSelect: () => void,
}

interface FileExplorerDialogProps {
  onClose?: () => void,
  onSelectFile?: (file: Document) => void,
  onDeleteFile?: (file: Document) => void,
  previewDialog?: (file: Document, handlers: PreviewControlHandlers) => React.ReactNode,
  title?: string,
  subtitle?: string,
  displayClose?: boolean,
  customClose?: React.ReactNode,
  filter?: string,
  excludes?: Array<string | number>,
  excludeOfficeDocuments?: boolean,
  tenantCollaborationId?: number,
  businessPartnerUnlinkedOnly?: boolean,
  showFolderNav?: boolean,
  compactSpacing?: boolean,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  fileToolbar: {
    padding: '10px 20px 40px',
  },
  compactFileToolbar: {
    padding: '0 12px 24px 32px',
  },
  emptyMessage: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  appstoreBadge: {
    height: `calc(100vh - ${theme.pageContentOffset}px)`,
  },
  toggleButton: {
    marginTop: 10,
    marginRight: 20,
  },
  compactToggleButton: {
    marginTop: 0,
    marginRight: 24,
  },
  listSwitch: {
    paddingRight: 19.5,
    paddingLeft: 19.5,
    height: 44,
  },
  active: {
    '& svg': {
      fill: theme.primaryDark,
    },
  },
  headerContent: {
    display: 'flex',
  },
  compactHeaderSpacing: {
    '& > div:first-child > div': {
      padding: '16px 0',
    },
  },
  compactPanel: {
    flex: '1 1 auto',
    height: 'auto',
    minHeight: 0,
  },
  tabInput: {
    marginTop: 8,
    marginLeft: 30,
    '& > div': {
      borderRadius: 5,
    },
  },
  contentWithFolderNav: {
    flexDirection: 'row',
    overflow: 'hidden',
  },
  fileListContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
}))

const FileExplorerDialog = (props: FileExplorerDialogProps) => {
  const {
    onClose = () => {},
    onSelectFile,
    onDeleteFile = () => {},
    title,
    subtitle,
    previewDialog,
    displayClose = true,
    customClose,
    filter = 'all',
    excludes = [],
    excludeOfficeDocuments = false,
    tenantCollaborationId,
    businessPartnerUnlinkedOnly = false,
    showFolderNav = false,
    compactSpacing = false,
  } = props

  const dispatch = useAppDispatch()
  const classes = useStyles()
  const state = useAppSelector(state => state.document.list)
  const {
    isLoading,
    items,
    pagination,
    options,
    listDisplayType,
    columns,
    shouldRefresh,
    currentFolder,
  } = state
  const {
    searchTerm,
  } = options
  const { sortOrder, orderBy } = pagination
  const columnsToShow = ['recordDate', 'name', 'tags', 'origin', 'mimetype'].map(col => {
    const colDev = columns.find(tmp => tmp.name === col) || {}
    return { ...colDev, active: true }
  })
  const [previewState, setPreviewState] = useState(INITIAL_PREVIEW_STATE)

  const doDisplayPreview = previewState.file !== null

  const resetPreviewState = () => setPreviewState(INITIAL_PREVIEW_STATE)

  const handleRefreshFiles = (options) => {
    // edge cases [bugs] in switching between list and thumbs - to be fixed later
    // when switching from list to thumbs, first 40 aren't fetched from the server, instead uses first 20 from the list, unless refreshed.
    // if pagination is used in the list view, thumbnail view wont' be updated, unless refreshed.
    const { offset } = options
    if (offset !== pageToRender.offset) {
      dispatch(setPagination({ offset }))
    }
  }

  const setCollaborationToken = useDmsCollaborationToken()

  useEffect(() => {
    setCollaborationToken(null).then(() => {
      dispatch(setActiveFilter({ filter, excludes }))
      dispatch(fetchListSetting())
      if (showFolderNav) {
        dispatch(fetchFolders())
      }
      fetch(true)
    })
    return () => {
      dispatch(clearList())
      dispatch(setActiveFilter({ filter: 'all', excludes: undefined }))
    }
  }, [])

  useEffect(() => {
    if (shouldRefresh) {
      fetch()
    }
  }, [pagination.offset, options.searchTerm, showFolderNav ? currentFolder?.id : null])

  const fetch = (force = false) => {
    if (force || (!isLoading && (!items.length || shouldRefresh) && isThumbsActive)) {
      if (force) {
        setFolder({}, { keepSearchTerm: true })
      }
      dispatch(fetchListAction({ queryType: QUERY_TYPES.ALL, ...pagination, ...options, filter: filter || 'notAssigned', excludes: excludes || [], businessPartnerUnlinkedOnly }))
    }
  }

  const handlePreviewFile = (file) => {
    setPreviewState({ file })
  }

  const handleClosePreview = () => {
    resetPreviewState()
  }

  const handleSelectFile = (fileInfo) => {
    onSelectFile?.(fileInfo)
  }
  const handleSelectFromPreview = () => {
    onSelectFile?.(previewState.file)
    resetPreviewState()
  }
  const handlePreviewRemove = () => {
    onDeleteFile(previewState.file)
  }
  const handlePreviewAbort = () => {
    resetPreviewState()
  }

  const previewControlHandlers = {
    handleClosePreview,
    handleRemove: handlePreviewRemove,
    handleAbort: handlePreviewAbort,
    handleSelect: handleSelectFromPreview,
  }

  const handleSearchChanged = (value) => {
    dispatch(setSearchTerm(value))
  }

  const handleSortChange = (optionRef) => {
    dispatch(setSortOrder(optionRef))
  }

  const isThumbsActive = listDisplayType === FILE_LIST_TYPE.thumbs
  const isListActive = !isThumbsActive

  const pageToRender = useMemo(() => {
    const files = sort(items, [orderBy], [sortOrder])

    const page = {
      ...pagination,
      files: excludeOfficeDocuments ? files.filter((file: Document) => !isMSOfficeDocument(file?.mimetype)) : files,
    }

    return page
  }, [pagination, items])

  const handleDisplayTypeChanged = (type) => {
    dispatch(setListDisplayType(type))
  }

  const switchDisplayType = (type) => {
    if (listDisplayType !== type) {
      handleDisplayTypeChanged(type)
    }
  }

  const listSwitchButtons = (
    <ButtonGroup className={clsx(classes.toggleButton, compactSpacing && classes.compactToggleButton)}
      disableRipple={true}
      disableFocusRipple={true}
    >
      <ToolbarButton
        title={lang.generics.list}
        disableRipple={true}
        onClick={() => switchDisplayType(FILE_LIST_TYPE.list)}
        className={clsx(classes.listSwitch, isListActive && classes.active)}
      >
        <SVGIcon svg={ListIcon} width={SIZES.ICON} height='auto' />
      </ToolbarButton>
      <ToolbarButton
        title={lang.generics.thumbnails}
        disableRipple={true}
        onClick={() => switchDisplayType(FILE_LIST_TYPE.thumbs)}
        className={clsx(classes.listSwitch, isThumbsActive && classes.active)}
      >
        <SVGIcon svg={TilesIcon} width={SIZES.ICON} height='auto' />
      </ToolbarButton>
    </ButtonGroup>
  )

  const onTabInputChange = (v) => {
    dispatch(cancelUploads())
    dispatch(terminateWorker())
    setCollaborationToken(v ? tenantCollaborationId : null, handleRefreshFiles)
  }

  const tabRef = useRef()
  const TAB_INDICES = {
    self: 0,
    customer: 1,
  }

  const fileListContent = (
    <React.Fragment>
      {(!isLoading && pageToRender.count === 0 && !searchTerm.length) && <AppStoreBadges className={classes.appstoreBadge} />}
      {(!isLoading && pageToRender.count === 0 && searchTerm.length > 0) && <div className={classes.emptyMessage}>{lang.globalSearch.emptyStateText}</div>}
      {isThumbsActive && <ThumbnailList
        size={200}
        disableFavButton={true}
        disableRightClickContextMenu={true}
        type={FILE_LIST_TYPE.thumbs}
        page={pageToRender}
        allowDrop={false}
        onClose={onClose}
        onSelectFile={handleSelectFile}
        onPreviewRequest={handlePreviewFile}
        onRefreshFiles={handleRefreshFiles} />}
      {isListActive && <FileFlatList
        columns={columnsToShow}
        disableRightClickContextMenu={true}
        multiSelect={false}
        type={FILE_LIST_TYPE.thumbs}
        page={pageToRender}
        onClose={onClose}
        onSelectFile={onSelectFile ? handleSelectFile : handlePreviewFile}
        onDeleteFile={onDeleteFile}
        fetchOptions={{ businessPartnerUnlinkedOnly }} />}
    </React.Fragment>
  )

  return (
    <React.Fragment>
      <Panel className={compactSpacing ? classes.compactPanel : undefined}>
        <PanelHeader
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          displayClose={displayClose}
          customClose={customClose}
          className={compactSpacing ? classes.compactHeaderSpacing : undefined}
        >
          <div className={classes.headerContent}>
            {tenantCollaborationId
              ? <TabInput
                ref={tabRef}
                key='toggleCollaboration'
                customClass={classes.tabInput}
                tabs={[
                  { key: 'self', label: lang.generics.taxConsultant, content: '' },
                  { key: 'customer', label: lang.generics.client, content: '' },
                ]}
                value={TAB_INDICES.self}
                onChange={onTabInputChange} />
              : null}
            <FileToolbar
              className={clsx(classes.fileToolbar, compactSpacing && classes.compactFileToolbar)}
              onSearchChanged={handleSearchChanged}
              onSortChange={handleSortChange}
              showQuickFilter={false}
              compactSpacing={compactSpacing}>
              {listSwitchButtons}
            </FileToolbar>
          </div>
        </PanelHeader>
        <PanelContent className={showFolderNav ? classes.contentWithFolderNav : undefined}>
          {showFolderNav
            ? (
              <React.Fragment>
                <FolderNav filter={filter} compactSpacing={compactSpacing} />
                <div className={classes.fileListContainer}>
                  {fileListContent}
                </div>
              </React.Fragment>
            )
            : fileListContent}
        </PanelContent>
      </Panel>
      {doDisplayPreview && previewDialog(previewState.file, previewControlHandlers)}
    </React.Fragment>
  )
}

export default FileExplorerDialog
